// ════════════════════════════════════════════════════════════════════
// Centre des agents et centre de notifications (P1, contrat §11) : fonctions pures, sans
// React ni accès au DOM (tests node). L'écran reste en français (décision 11 de la P1) :
// la langue d'un agent ne vaut que pour ses messages, e-mails, SMS et rapports.
//   - notifications : normalisation, fusion (liste relue et flux temps réel), tri, compte
//     des non-lues, délai de reconnexion du flux ;
//   - agents : états → tons de Badge, familles, tri, filtre, mission en clair, verdicts ;
//   - bibliothèque des 12 modèles du cahier (section « Familles d'agents et bibliothèque »),
//     chacun avec la phrase de demande envoyée dans la conversation : la carte « Nouvel
//     agent » reste obligatoire (D4), un modèle ne crée jamais rien par lui-même.
// Les tons sont ceux du composant Badge (prim, info, warn, success, danger, neutral) :
// utils/statusTone.js, dictionnaire fermé recopié côté serveur, n'est pas étendu ici.
// ════════════════════════════════════════════════════════════════════

import { texteUnique } from './agentSpec.js';

// ── Notifications ────────────────────────────────────────────────────

// Gravités du moteur (table notifications) ; rang : ordre d'affichage des non-lues
export const GRAVITES = Object.freeze({
  critique: { texte: 'Critique', ton: 'danger', rang: 0 },
  attention: { texte: 'Attention', ton: 'warn', rang: 1 },
  information: { texte: 'Information', ton: 'info', rang: 2 },
});
export const graviteDe = (gravite) => GRAVITES[gravite] ?? GRAVITES.information;

// Types écrits par le moteur ; un type inconnu (version plus récente du serveur) reste affiché
export const TYPES_NOTIFICATION = Object.freeze({
  alerte: 'Alerte',
  aggravation: 'Aggravation',
  fermeture: 'Retour à la normale',
  echecs: 'Échecs répétés',
  proprietaire: 'Action requise',
  rapport: 'Rapport',
  information: 'Information',
});

// Liste gardée en mémoire : la plus récente d'abord, bornée (le serveur garde le reste)
export const NOTIFICATIONS_MAX = 100;
// Toast d'une alerte critique : seulement si elle vient d'arriver (jamais pour un arriéré relu)
export const CRITIQUE_RECENTE_MS = 10 * 60 * 1000;

const texte = (value, max) => (typeof value === 'string' ? value.trim().slice(0, max) : '');
const entier = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isSafeInteger(n) ? n : null;
};
const instant = (value) => {
  if (typeof value !== 'string' || !value) return null;
  return Number.isNaN(Date.parse(value)) ? null : value;
};
const temps = (iso) => (iso ? Date.parse(iso) : 0) || 0;

/**
 * Notification du serveur (liste ou événement du flux) dans la forme du contrat §5 :
 * {id, curseur, type, gravite, titre, lien, agent_id, alerte_id, cree_le, lue_le, accusee}.
 * Sans identifiant : null (ignorée). Valeurs hors contrat ramenées à un défaut lisible.
 */
export function normaliserNotification(brut) {
  if (!brut || typeof brut !== 'object') return null;
  const id = brut.id === null || brut.id === undefined ? '' : String(brut.id).trim();
  if (!id) return null;
  return {
    id,
    curseur: entier(brut.curseur),
    type: texte(brut.type, 40) || 'information',
    gravite: GRAVITES[brut.gravite] ? brut.gravite : 'information',
    titre: texte(brut.titre, 200) || 'Notification',
    lien: texte(brut.lien, 500) || null,
    agent_id: brut.agent_id === null || brut.agent_id === undefined ? null : String(brut.agent_id),
    alerte_id: brut.alerte_id === null || brut.alerte_id === undefined ? null : String(brut.alerte_id),
    cree_le: instant(brut.cree_le),
    lue_le: instant(brut.lue_le),
    accusee: brut.accusee === true || brut.alerte_statut === 'accusee' || brut.alerte_statut === 'close',
  };
}

/** Une alerte encore ouverte se « prend en compte » (accusé, POST /api/alertes/:id/accuse). */
export const estAccusable = (n) =>
  !!n && (n.type === 'alerte' || n.type === 'aggravation') && !!n.alerte_id && !n.accusee;

/**
 * Ordre d'affichage : les non-lues d'abord, classées par gravité puis de la plus récente à la
 * plus ancienne ; les lues ensuite, par date seulement. Un classement par gravité seule
 * garderait une vieille alerte critique déjà traitée au-dessus de tout ce qui arrive.
 * estNonLue : le tiroir ouvert y passe l'état lu à son ouverture, pour qu'une notification
 * marquée comme lue ne saute pas en bas de la liste sous le focus de l'utilisateur.
 */
export function trierNotifications(items, estNonLue = (n) => !n.lue_le) {
  return [...items].sort((a, b) => {
    const lueA = estNonLue(a) ? 0 : 1;
    const lueB = estNonLue(b) ? 0 : 1;
    if (lueA !== lueB) return lueA - lueB;
    if (!lueA) {
      const rang = graviteDe(a.gravite).rang - graviteDe(b.gravite).rang;
      if (rang) return rang;
    }
    return temps(b.cree_le) - temps(a.cree_le) || (b.curseur ?? 0) - (a.curseur ?? 0);
  });
}

/**
 * Fusion par identifiant (liste relue, événements du flux, réponse d'une action) :
 * l'arrivée l'emporte champ par champ, sauf qu'une notification lue ou une alerte prise en
 * compte le reste (une relecture partie avant le clic ne la rend pas de nouveau non lue).
 * → {items (triés, NOTIFICATIONS_MAX au plus), nouvelles (identifiants inconnus jusque-là)}
 */
export function fusionnerNotifications(actuelles, arrivees, { max = NOTIFICATIONS_MAX } = {}) {
  const parId = new Map(actuelles.map((n) => [n.id, n]));
  const nouvelles = [];
  for (const brut of arrivees) {
    const n = normaliserNotification(brut);
    if (!n) continue;
    const avant = parId.get(n.id);
    if (!avant) {
      nouvelles.push(n);
      parId.set(n.id, n);
      continue;
    }
    parId.set(n.id, {
      ...avant,
      ...n,
      curseur: n.curseur ?? avant.curseur,
      lue_le: n.lue_le ?? avant.lue_le,
      accusee: n.accusee || avant.accusee,
    });
  }
  return { items: trierNotifications([...parId.values()]).slice(0, max), nouvelles };
}

export const compterNonLues = (items) => items.filter((n) => !n.lue_le).length;

/** Curseur de reprise du flux : le plus grand rang connu (ou celui déjà retenu). */
export function curseurMax(items, actuel = null) {
  return items.reduce((max, n) => (n.curseur !== null && (max === null || n.curseur > max) ? n.curseur : max), actuel);
}

/**
 * Alertes critiques à signaler par un toast : nouvelles, non lues, arrivées depuis peu
 * (une relecture qui ramène un arriéré ne déclenche aucun toast).
 */
export function critiquesASignaler(nouvelles, maintenant = Date.now()) {
  return nouvelles.filter((n) => n.gravite === 'critique' && !n.lue_le && (!n.cree_le || maintenant - temps(n.cree_le) <= CRITIQUE_RECENTE_MS));
}

// Reconnexion du flux : 1 s, 2 s, 4 s… jusqu'à 60 s, ± 20 % pour que les onglets d'une même
// entreprise ne reviennent pas tous à la même seconde après une coupure du serveur
export const RECONNEXION_BASE_MS = 1000;
export const RECONNEXION_MAX_MS = 60 * 1000;
export function delaiReconnexion(tentative, alea = Math.random) {
  const n = Math.max(0, Math.min(Number(tentative) || 0, 16));
  const base = Math.min(RECONNEXION_MAX_MS, RECONNEXION_BASE_MS * 2 ** n);
  return Math.round(base * (0.8 + 0.4 * alea()));
}

/** Nom accessible de la cloche (contrat §11.1) : « Notifications : 3 non lues ». */
export function libelleCloche(nonLues) {
  const n = Math.max(0, Number(nonLues) || 0);
  if (n === 0) return 'Notifications : aucune non lue';
  return `Notifications : ${n} non lue${n > 1 ? 's' : ''}`;
}

// Badge de la cloche : au-delà de 99, « 99+ » (le nom accessible garde le nombre exact)
export const compteBadge = (nonLues) => (nonLues > 99 ? '99+' : String(nonLues));

/**
 * Pastille du bouton « Agents » : agents actifs de l'utilisateur, les siens et ses abonnements,
 * comptés une fois (liste normalisée par normaliserListeAgents).
 */
export function compteAgentsActifs({ mesAgents = [], abonnements = [] } = {}) {
  const actifs = new Set();
  for (const a of [...mesAgents, ...abonnements]) if (a?.etat === 'actif' && a.id) actifs.add(a.id);
  return actifs.size;
}

/** Nom accessible du bouton « Agents » : « Agents : 2 actifs ». */
export function libelleBoutonAgents(actifs) {
  const n = Math.max(0, Number(actifs) || 0);
  if (n === 0) return 'Agents : aucun actif';
  return `Agents : ${n} actif${n > 1 ? 's' : ''}`;
}

/**
 * Lien d'un rapport reçu : seulement /download/… ou /preview/… du même domaine (liens du
 * moteur de documents), rendu relatif ; tout autre lien (autre domaine, javascript:…) : null.
 */
export function lienRapport(lien, origine = 'http://localhost') {
  if (typeof lien !== 'string' || !lien.trim()) return null;
  try {
    const base = new URL(origine);
    const url = new URL(lien.trim(), base);
    if (url.origin !== base.origin) return null;
    if (!/^\/(download|preview)\//.test(url.pathname)) return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

// ── Dates et durées (heure locale du navigateur, comme le reste de l'écran) ──

const HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });
const JOUR = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });
const JOUR_ANNEE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_LONGUE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
const NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
const UNE_DECIMALE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

const memeJour = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** « à l'instant », « il y a 5 min », « aujourd'hui à 14:05 », « hier à 09:30 », « le 12 oct. à 08:00 ». */
export function formaterQuand(iso, maintenant = Date.now()) {
  const t = temps(iso);
  if (!t) return '';
  const ecart = maintenant - t;
  if (ecart >= 0 && ecart < 60 * 1000) return 'à l’instant';
  if (ecart >= 0 && ecart < 60 * 60 * 1000) return `il y a ${Math.floor(ecart / 60000)} min`;
  const date = new Date(t);
  const jour = new Date(maintenant);
  if (memeJour(date, jour)) return `aujourd’hui à ${HEURE.format(date)}`;
  const veille = new Date(maintenant);
  veille.setDate(veille.getDate() - 1);
  if (memeJour(date, veille)) return `hier à ${HEURE.format(date)}`;
  const format = date.getFullYear() === jour.getFullYear() ? JOUR : JOUR_ANNEE;
  return `le ${format.format(date)} à ${HEURE.format(date)}`;
}

/** Exécution en cours : « depuis moins d'une minute », « depuis 12 min », « depuis 14:05 ». */
export function formaterDepuis(iso, maintenant = Date.now()) {
  const t = temps(iso);
  if (!t) return '';
  const ecart = Math.max(0, maintenant - t);
  if (ecart < 60 * 1000) return 'depuis moins d’une minute';
  if (ecart < 60 * 60 * 1000) return `depuis ${Math.floor(ecart / 60000)} min`;
  return `depuis ${HEURE.format(new Date(t))}`;
}

/** Durée d'une exécution : « 850 ms », « 4,2 s », « 1 min 05 s ». */
export function formaterDuree(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60000) return `${UNE_DECIMALE.format(n / 1000)} s`;
  const secondes = Math.round(n / 1000);
  return `${Math.floor(secondes / 60)} min ${String(secondes % 60).padStart(2, '0')} s`;
}

// ── Agents ───────────────────────────────────────────────────────────

// États du moteur (table agents) → texte et ton de Badge ; rang : ordre des listes (ce qui
// demande une action d'abord)
export const ETATS_AGENT = Object.freeze({
  en_erreur: { texte: 'En erreur', ton: 'danger', rang: 0 },
  en_attente_proprietaire: { texte: 'En attente du propriétaire', ton: 'warn', rang: 1 },
  actif: { texte: 'Actif', ton: 'success', rang: 2 },
  en_pause: { texte: 'En pause', ton: 'neutral', rang: 3 },
  termine: { texte: 'Terminé', ton: 'info', rang: 4 },
  supprime: { texte: 'Supprimé', ton: 'neutral', rang: 5 },
});
export const etatAgent = (etat) => ETATS_AGENT[etat] ?? { texte: 'État inconnu', ton: 'neutral', rang: 6 };

// Quatre familles (cahier) : elles diffèrent par leur déclencheur et leur durée de vie
export const FAMILLES = Object.freeze({
  veilleur: { texte: 'Veilleur', pluriel: 'Veilleurs', description: 'contrôle une règle à intervalle fixe' },
  sentinelle: { texte: 'Sentinelle', pluriel: 'Sentinelles', description: 'réagit à un événement de la plateforme' },
  rapporteur: { texte: 'Rapporteur', pluriel: 'Rapporteurs', description: 'envoie un rapport selon un calendrier' },
  analyste: { texte: 'Analyste', pluriel: 'Analystes', description: 'mène une analyse de fond, une seule fois' },
});
export const familleDe = (famille) => FAMILLES[famille] ?? { texte: 'Agent', pluriel: 'Agents', description: '' };

// Verdicts d'une exécution (table executions) ; null : exécution en cours
export const VERDICTS = Object.freeze({
  rien: { texte: 'Rien à signaler', ton: 'neutral' },
  alerte: { texte: 'Alerte', ton: 'warn' },
  aggravation: { texte: 'Aggravation', ton: 'danger' },
  fermeture: { texte: 'Retour à la normale', ton: 'success' },
  erreur: { texte: 'Erreur', ton: 'danger' },
  ignoree: { texte: 'Ignorée', ton: 'neutral' },
});
// Exécution d'un rapporteur (verdict « rien » du moteur) : elle lance son rapport périodique, ce
// n'est pas un contrôle « sans rien à signaler ». Rapporteur : famille rapporteur, ou règle
// « aucune » quelle que soit la famille (même lecture que le moteur ; rapporteur : vrai la force)
const RAPPORT_LANCE = Object.freeze({ texte: 'Rapport lancé', ton: 'info' });
export const verdictExecution = (verdict, famille = null, { rapporteur = famille === 'rapporteur' } = {}) => {
  if (!verdict) return { texte: 'En cours', ton: 'info' };
  if (rapporteur && verdict === 'rien') return RAPPORT_LANCE;
  return VERDICTS[verdict] ?? { texte: verdict, ton: 'neutral' };
};

const exec = (brut) => {
  if (!brut) return null;
  if (typeof brut === 'string') return instant(brut) ? { debut: brut, verdict: null } : null;
  if (typeof brut !== 'object') return null;
  const debut = instant(brut.debut ?? brut.le ?? null);
  return debut || brut.verdict ? { debut, verdict: typeof brut.verdict === 'string' ? brut.verdict : null } : null;
};

/**
 * Agent d'une liste (GET /api/agents) ou d'une fiche, dans une forme stable pour l'écran.
 * Lecture tolérante des champs du serveur (le contrat ne fixe que les listes) :
 * {id, nom, famille, etat, raisonEtat, role, derniereExecution, prochaineExecution,
 *  alertes30j, enCours, sourdineJusquAu}. Sans identifiant : null.
 */
export function normaliserAgent(brut, role = null) {
  if (!brut || typeof brut !== 'object') return null;
  const id = String(brut.id ?? brut.agent_id ?? '').trim();
  if (!id) return null;
  const enCours = brut.en_cours ?? brut.execution_en_cours ?? null;
  return {
    id,
    nom: texte(brut.nom, 120) || 'Agent sans nom',
    famille: texte(brut.famille, 20) || null,
    etat: texte(brut.etat, 40) || null,
    raisonEtat: texte(brut.raison_etat, 64) || null,
    role: brut.role === 'abonne' || brut.role === 'proprietaire' ? brut.role : role,
    derniereExecution: exec(brut.derniere_execution ?? brut.derniere_execution_le ?? null),
    prochaineExecution: instant(brut.prochaine_execution ?? brut.prochain_creneau ?? null),
    alertes30j: entier(brut.alertes_30j),
    enCours: enCours ? { debut: instant(enCours.debut ?? null), appels: entier(enCours.appels) } : null,
    sourdineJusquAu: instant(brut.sourdine_jusqu_au ?? null),
  };
}

/** Ce qui demande une action d'abord (erreur, attente du propriétaire), puis par nom. */
export function trierAgents(agents) {
  return [...agents].sort((a, b) => etatAgent(a.etat).rang - etatAgent(b.etat).rang || a.nom.localeCompare(b.nom, 'fr'));
}

/** GET /api/agents → {mesAgents, abonnements} triés (l'onglet « Entreprise » reste masqué en P1). */
export function normaliserListeAgents(reponse) {
  const liste = (valeur, role) => (Array.isArray(valeur) ? valeur : []).map((a) => normaliserAgent(a, role)).filter(Boolean);
  return {
    mesAgents: trierAgents(liste(reponse?.mes_agents, 'proprietaire')),
    abonnements: trierAgents(liste(reponse?.abonnements, 'abonne')),
  };
}

const sansAccents = (value) => String(value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Filtre du centre : nom de l'agent, famille ou état, sans tenir compte des accents ni de la casse. */
export function filtrerAgents(agents, recherche) {
  const q = sansAccents(recherche).trim();
  if (!q) return agents;
  return agents.filter((a) => [a.nom, familleDe(a.famille).texte, etatAgent(a.etat).texte].some((champ) => sansAccents(champ).includes(q)));
}

/** Onglet « En cours » : agents dont une exécution tourne (propriétaire ou abonné, sans doublon). */
export function agentsEnCours({ mesAgents = [], abonnements = [] } = {}) {
  const vus = new Set();
  return [...mesAgents, ...abonnements].filter((a) => a.enCours && !vus.has(a.id) && vus.add(a.id));
}

/**
 * Rôle de l'utilisateur pour un agent d'après sa liste normalisée (normaliserListeAgents) :
 * 'proprietaire', 'abonne', ou null s'il n'y figure pas (agent supprimé, accès retiré).
 */
export function roleDansListe(liste, agentId) {
  const id = String(agentId ?? '');
  if (!id || !liste) return null;
  if ((liste.mesAgents ?? []).some((a) => a.id === id)) return 'proprietaire';
  if ((liste.abonnements ?? []).some((a) => a.id === id)) return 'abonne';
  return null;
}

// ── Panneau latéral : fiche d'un agent, rapport reçu ────────────────

/**
 * Artefact de la fiche d'un agent (visionneuse « agent » du registre) : identifiant stable par
 * agent, si bien que rouvrir la fiche affichée ne la recharge pas. Depuis une notification, le
 * nom n'est pas connu : titre provisoire « Agent », remplacé par la fiche dès sa lecture.
 */
export function artefactAgent({ id, nom = null, famille = null, role = null } = {}) {
  const agentId = String(id ?? '').trim();
  if (!agentId) return null;
  return {
    id: `agent:${agentId}`,
    type: 'agent',
    title: nom || 'Agent',
    subtitle: famille ? familleDe(famille).texte : null,
    payload: { agentId, role: role === 'abonne' || role === 'proprietaire' ? role : null },
  };
}

/**
 * Artefact d'un rapport reçu (notification « Rapport prêt », rapports de la fiche), affiché par
 * la visionneuse « Document » existante comme dans le fil (cahier : rapport « ouvert dans le
 * panneau »). Lien /download/… ou /preview/… du même domaine seulement (lienRapport) ; pour un
 * PDF, téléchargement et aperçu partagent dossier, identifiant et nom (documents/storage.js).
 * Origine « agent » : rapport gardé 7 jours, échéance dite en jours (documentSpec.js).
 * Lien refusé : null.
 */
export function artefactRapport({ id = null, lien, titre = null, creeLe = null } = {}, origine = 'http://localhost') {
  const chemin = lienRapport(lien, origine);
  if (!chemin) return null;
  const format = (chemin.split('?')[0].match(/\.([a-z0-9]+)$/i)?.[1] ?? 'pdf').toLowerCase();
  const telechargement = chemin.replace(/^\/preview\//, '/download/');
  const titreRapport = texte(titre, 200) || 'Rapport d’expertise';
  return {
    id: `rapport:${id || telechargement}`,
    type: 'document',
    title: titreRapport,
    subtitle: 'Rapport d’agent',
    payload: {
      spec: {
        kind: 'document',
        format,
        title: titreRapport,
        download_url: telechargement,
        ...(format === 'pdf' ? { preview_url: telechargement.replace(/^\/download\//, '/preview/') } : {}),
        origin: 'agent',
        ...(instant(creeLe) ? { generated_at: creeLe } : {}),
      },
    },
  };
}

// ── Mission en clair (fiche d'un agent) ─────────────────────────────

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const COMPARAISONS = { '>': 'plus de', '>=': 'au moins', '<': 'moins de', '<=': 'au plus', '=': 'exactement' };
const CANAUX = { application: 'application', push: 'navigateur', email: 'e-mail', sms: 'SMS', whatsapp: 'WhatsApp' };
const majuscule = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

function texteIntervalle(minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return null;
  if (m === 1) return 'Toutes les minutes';
  if (m === 1440) return 'Tous les jours';
  if (m % 1440 === 0) return `Tous les ${m / 1440} jours`;
  if (m === 60) return 'Toutes les heures';
  if (m % 60 === 0) return `Toutes les ${m / 60} heures`;
  return `Toutes les ${m} minutes`;
}

// Calendriers usuels en clair (fuseau Africa/Abidjan, D11) ; les autres restent en cron
function texteCron(cron) {
  if (typeof cron !== 'string' || !cron.trim()) return null;
  const brut = `Calendrier : ${cron.trim()}`;
  const champs = cron.trim().split(/\s+/);
  if (champs.length !== 5) return brut;
  const [minute, heure, jourMois, mois, jourSemaine] = champs;
  if (mois !== '*' || !/^\d{1,2}$/.test(minute) || !/^\d{1,2}$/.test(heure)) return brut;
  const h = `${Number(heure)} h${Number(minute) ? ` ${String(Number(minute)).padStart(2, '0')}` : ''}`;
  if (jourMois === '*' && jourSemaine === '*') return `Chaque jour à ${h}`;
  if (jourMois === '*' && /^[0-7]$/.test(jourSemaine)) return `Chaque ${JOURS[Number(jourSemaine) % 7]} à ${h}`;
  if (jourMois === '*' && jourSemaine === '1-5') return `Du lundi au vendredi à ${h}`;
  if (/^\d{1,2}$/.test(jourMois) && jourSemaine === '*') return `Le ${jourMois === '1' ? '1er' : Number(jourMois)} du mois à ${h}`;
  return brut;
}

function texteDeclencheur(d, maintenant = Date.now()) {
  if (!d || typeof d !== 'object') return null;
  if (d.type === 'intervalle') return texteIntervalle(d.minutes);
  if (d.type === 'calendrier') return texteCron(d.cron);
  if (d.type === 'evenement') {
    const types = Array.isArray(d.types) ? d.types.filter((t) => typeof t === 'string') : [];
    return types.length ? `À chaque événement de la plateforme (${types.join(', ')})` : 'À chaque événement de la plateforme';
  }
  if (d.type === 'unique') return texteUnique(d, maintenant);
  return null;
}

// Rappel d'une alerte qui dure (verdict du moteur) : rearmement_min minutes, sinon rearmement_h
// heures ; seulement quand la mission porte ses options (jamais inventé) et qu'elle alerte, jamais
// pour une exécution unique (faite une fois). Une sentinelle signale des apparitions, pas un
// dépassement de seuil : la phrase s'arrête au délai, comme sur la carte (carteAgent.js du serveur)
function texteRappel(mission) {
  const o = mission.options && typeof mission.options === 'object' ? mission.options : null;
  if (!o || mission.famille === 'rapporteur' || mission.regle?.type === 'aucune' || mission.declencheur?.type === 'unique') return null;
  const minutes = Number.isFinite(o.rearmement_min) ? o.rearmement_min : Number.isFinite(o.rearmement_h) ? o.rearmement_h * 60 : null;
  if (!minutes || minutes <= 0) return null;
  const delai = minutes === 1440 ? '24 h' : minutes >= 2880 && minutes % 1440 === 0 ? `${minutes / 1440} jours` : minutes >= 60 ? `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${String(minutes % 60).padStart(2, '0')}` : ''}` : `${minutes} min`;
  if (mission.famille === 'sentinelle') return `Une alerte par élément, renouvelée après ${delai}`;
  const pct = Number.isFinite(o.aggravation_pct) ? NOMBRE.format(o.aggravation_pct) : '20';
  return `Une alerte par élément, renouvelée après ${delai} ou si la situation s’aggrave de ${pct} %`;
}

// Unité qui compte des faits (« amendes », « survitesses ») : « plus de 0 » s'écrit « au moins 1
// amende », comme les e-mails et la carte (uniteDeCompte de backend/agents/canaux/messages.js) ;
// mots invariables (« mois », « bus ») et unités de mesure (« litres ») exclus
const PLURIEL_RE = /^\p{Ll}[\p{L}-]*\p{L}s$/u;
const INVARIABLE_RE = /(?:ss|(?<![eo])us|ès|ais|ois)$/u;
const UNITES_INVARIABLES = new Set(['mois', 'fois', 'pas', 'cas', 'temps', 'poids', 'frais', 'avis', 'colis', 'devis', 'permis', 'pays', 'cours', 'parcours', 'sms', 'gps']);
const UNITES_MESURE_RE = /^(?:litres?|m[eè]tres?|kilom[eè]tres?|miles?|heures?|minutes?|secondes?|jours?|semaines?|ans?|ann[ée]es?|euros?|dollars?|francs?|tonnes?|kilos?|kilogrammes?|grammes?|degr[ée]s?|points?|centimes?|watts?|kilowatts?|gallons?)$/iu;
const uniteDeCompte = (u) => typeof u === 'string' && PLURIEL_RE.test(u) && !UNITES_INVARIABLES.has(u) && !INVARIABLE_RE.test(u) && !UNITES_MESURE_RE.test(u);

// declencheur : celui de la mission (une exécution unique fait un compte rendu, une fois)
function texteRegle(r, declencheur = null) {
  if (!r || typeof r !== 'object') return null;
  if (r.type === 'aucune') return declencheur?.type === 'unique' ? 'Aucune règle : un compte rendu après l’exécution' : 'Aucune règle : un envoi à chaque échéance';
  if (r.type === 'jugement') return r.condition ? `Jugement du modèle : « ${texte(r.condition, 300)} »` : 'Jugement du modèle';
  if (r.type !== 'expression') return null;
  if (typeof r.seuil !== 'number' || !Number.isFinite(r.seuil)) return 'Règle chiffrée';
  const unite = texte(r.unite, 40);
  if ((r.comparaison ?? '>') === '>' && r.seuil === 0 && uniteDeCompte(unite)) return `Au moins 1 ${unite.slice(0, -1)}`;
  const comparaison = COMPARAISONS[r.comparaison] ?? (r.sens === 'baisse' ? 'moins de' : 'plus de');
  return majuscule(`${comparaison} ${NOMBRE.format(r.seuil)}${unite ? ` ${unite}` : ''}`);
}

// Familles dont chaque alerte est suivie d'un rapport PDF sauf refus (livrable.rapport = false),
// missions enregistrées avant ce défaut comprises (rapport: null) : lecture du moteur
// (rapportApresAlerte, backend/agents/mission.js)
const FAMILLES_RAPPORT_DEFAUT = ['veilleur', 'sentinelle', 'analyste'];

function texteLivrable(l, mission = {}) {
  if (mission.famille === 'rapporteur' || mission.regle?.type === 'aucune') return mission.declencheur?.type === 'unique' ? 'Rapport PDF après l’exécution' : 'Rapport PDF à chaque exécution';
  if (!l || typeof l !== 'object') return null;
  const objet = l.rapport && typeof l.rapport === 'object';
  const avecRapport = l.rapport !== false && (objet || FAMILLES_RAPPORT_DEFAUT.includes(mission.famille));
  const rapport = avecRapport ? `rapport ${String((objet && l.rapport.format) || 'pdf').toUpperCase()}` : null;
  const parts = [l.message ? 'message' : null, rapport].filter(Boolean);
  return parts.length ? majuscule(parts.join(' et ')) : null;
}

function texteCanaux(canaux) {
  const liste = (Array.isArray(canaux) ? canaux : []).map((c) => CANAUX[c] ?? null).filter(Boolean);
  return liste.length ? majuscule(liste.join(', ')) : null;
}

function texteDuree(d) {
  if (!d || typeof d !== 'object') return null;
  if (d.type === 'permanente') return 'Sans fin';
  if (d.type === 'jusqu_au' && /^\d{4}-\d{2}-\d{2}$/.test(d.date ?? '')) {
    const date = new Date(`${d.date}T12:00:00Z`);
    return Number.isNaN(date.getTime()) ? null : `Jusqu’au ${DATE_LONGUE.format(date)}`;
  }
  if (d.type === 'envois' && Number.isFinite(Number(d.nombre))) {
    const n = Number(d.nombre);
    return `${n} envoi${n > 1 ? 's' : ''}`;
  }
  return null;
}

/**
 * Mission (schéma v1 de la P0) en lignes lisibles pour la fiche : déclencheur, règle,
 * livrable, rappel, canaux, durée. Une partie absente ou illisible est omise, jamais inventée ;
 * une exécution unique n'a ni rappel ni durée. maintenant : instant de la lecture (année de
 * l'heure d'une exécution unique). → [{cle, libelle, valeur}]
 */
export function resumeMission(mission, { canaux = null, maintenant = Date.now() } = {}) {
  if (!mission || typeof mission !== 'object') return [];
  const unique = mission.declencheur?.type === 'unique';
  return [
    ['declencheur', 'Déclencheur', texteDeclencheur(mission.declencheur, maintenant)],
    ['regle', 'Règle', texteRegle(mission.regle, mission.declencheur)],
    ['livrable', 'Livrable', texteLivrable(mission.livrable, mission)],
    ['rappel', 'Rappel', texteRappel(mission)],
    ['canaux', 'Canaux', texteCanaux(canaux ?? mission.canaux)],
    ['duree', 'Durée', unique ? null : texteDuree(mission.duree)],
  ].filter(([, , valeur]) => valeur).map(([cle, libelle, valeur]) => ({ cle, libelle, valeur }));
}

const lireJson = (valeur) => {
  if (typeof valeur !== 'string') return valeur ?? null;
  try {
    return JSON.parse(valeur);
  } catch {
    return null;
  }
};

const libelleAbonne = (a) => {
  if (typeof a === 'string') return texte(a, 120) || 'Abonné';
  if (!a || typeof a !== 'object') return 'Abonné';
  if (a.moi === true) return 'Vous';
  return texte(a.libelle ?? a.nom ?? a.email_masque ?? a.utilisateur_masque ?? '', 120) || 'Abonné';
};

/**
 * Fiche d'un agent (GET /api/agents/:id : {agent, version, abonnes, alertes_30j,
 * prochaine_execution, courbe}) dans une forme stable pour l'écran ; lecture tolérante
 * (version en nombre ou en objet, abonnés en nombre ou en liste, mission en JSON ou en
 * texte). role : rôle connu de la liste d'où vient la fiche, à défaut de celui du serveur ;
 * inconnu des deux (fiche ouverte depuis une notification) : null, jamais « propriétaire »
 * par défaut (la fiche le cherche alors dans la liste de l'utilisateur, roleDansListe).
 * Sans agent lisible : null.
 */
export function normaliserFiche(data, { role = null } = {}) {
  if (!data || typeof data !== 'object') return null;
  const agent = normaliserAgent(data.agent ?? null);
  if (!agent) return null;
  const version = data.version && typeof data.version === 'object' ? data.version : null;
  const mission = lireJson(version?.mission ?? data.mission ?? null);
  const canaux = lireJson(version?.canaux ?? null) ?? mission?.canaux ?? null;
  // Mission en clair calculée par le serveur (comme la carte), sinon déduite ici
  const resume = data.resume ?? version?.resume;
  const lignes = Array.isArray(resume) && resume.some((t) => typeof t === 'string' && t.trim())
    ? resume.filter((t) => typeof t === 'string' && t.trim()).map((valeur, i) => ({ cle: `resume-${i}`, libelle: null, valeur }))
    : resumeMission(mission, { canaux });
  const roleServeur = data.role ?? data.agent?.role
    ?? (data.est_proprietaire === true ? 'proprietaire' : data.est_proprietaire === false ? 'abonne' : null);
  const abonnes = Array.isArray(data.abonnes) ? data.abonnes : null;
  const points = Array.isArray(data.courbe?.points)
    ? data.courbe.points.filter((p) => p && typeof p.valeur === 'number' && Number.isFinite(p.valeur)).slice(-100)
    : [];
  return {
    agent: {
      ...agent,
      role: [roleServeur, role].find((r) => r === 'abonne' || r === 'proprietaire') ?? null,
      prochaineExecution: instant(data.prochaine_execution ?? null) ?? agent.prochaineExecution,
    },
    // Rapporteur (famille rapporteur ou règle « aucune ») : ses exécutions lancent un rapport
    rapporteur: agent.famille === 'rapporteur' || mission?.regle?.type === 'aucune',
    version: entier(version?.version) ?? entier(data.version) ?? entier(data.agent?.version_courante),
    langue: version?.langue === 'en' ? 'en' : 'fr',
    brief: texte(version?.brief ?? data.brief ?? '', 8000) || null,
    lignes,
    nbAbonnes: abonnes ? abonnes.length : entier(data.abonnes),
    abonnes: abonnes ? abonnes.map(libelleAbonne) : [],
    alertes30j: entier(data.alertes_30j) ?? (Array.isArray(data.alertes_30j) ? data.alertes_30j.length : agent.alertes30j),
    courbe: points.length ? { seuil: typeof data.courbe.seuil === 'number' ? data.courbe.seuil : null, points } : null,
    rapports: (Array.isArray(data.rapports) ? data.rapports : [])
      .map((r) => ({ id: String(r?.id ?? ''), lien: r?.lien ?? r?.document ?? r?.download_url ?? null, creeLe: instant(r?.cree_le ?? null), pages: entier(r?.pages) }))
      .filter((r) => r.lien),
  };
}

// Codes d'erreur d'exécution dits en clair dans la chronologie (le code reste affiché à côté)
const ERREURS_EN_CLAIR = Object.freeze({
  MCP_PAGE_HORS_LIMITES: 'page demandée au-delà de la dernière : lecture incomplète, rien n’a été conclu',
  CONTRAT_OUTIL_CHANGE: 'un outil a changé : agent en pause, à revoir',
});
export const erreurEnClair = (code) => (typeof code === 'string' ? ERREURS_EN_CLAIR[code] ?? null : null);

/**
 * Exécution de la chronologie (GET /api/agents/:id/executions) ; vides : listes vides dont la
 * plateforme a dit pourquoi ({etape, code, raison}), seulement quand le serveur en rend.
 */
export function normaliserExecution(brut) {
  if (!brut || typeof brut !== 'object') return null;
  const debut = instant(brut.debut ?? null);
  if (!debut) return null;
  const vides = (Array.isArray(brut.vides) ? brut.vides : [])
    .filter((v) => v && typeof v === 'object' && texte(v.code, 64))
    .slice(0, 10)
    .map((v) => ({ etape: texte(v.etape, 64) || null, code: texte(v.code, 64), raison: texte(v.raison, 300) || null }));
  return {
    debut,
    verdict: typeof brut.verdict === 'string' ? brut.verdict : null,
    appels: entier(brut.appels ?? brut.appels_outils),
    dureeMs: entier(brut.duree_ms),
    erreur: texte(brut.erreur, 64) || null,
    ...(vides.length ? { vides } : {}),
  };
}

/**
 * Message neutre d'une action refusée (fiche, centre) : la phrase du serveur pour un refus
 * métier ({error, code}), sinon un texte sans détail interne.
 */
export function messageErreur(erreur) {
  if (!erreur) return 'L’action n’a pas abouti : réessayez.';
  if (erreur.status === 0 || erreur.code === 'NETWORK') return 'Serveur injoignable : réessayez au retour du réseau.';
  if (erreur.status === 404) return 'Cet agent n’existe plus ou ne vous est plus accessible.';
  if (erreur.status === 503) return 'Le service des agents est momentanément indisponible : réessayez dans un instant.';
  if (erreur.status >= 400 && erreur.status < 500 && erreur.code && typeof erreur.message === 'string' && erreur.message && !/^HTTP \d+$/.test(erreur.message)) {
    return erreur.message;
  }
  return 'L’action n’a pas abouti : réessayez.';
}

/**
 * Résumé de la courbe des valeurs observées, lu à la place du dessin par les lecteurs
 * d'écran : nombre de contrôles, seuil, déclenchements, dernière valeur.
 */
export function resumeCourbe(courbe) {
  const points = Array.isArray(courbe?.points) ? courbe.points.filter((p) => typeof p?.valeur === 'number') : [];
  if (points.length < 2) return '';
  const declenchements = points.filter((p) => p.declenche === true).length;
  const seuil = typeof courbe.seuil === 'number' ? `, seuil ${NOMBRE.format(courbe.seuil)}` : '';
  const derniere = NOMBRE.format(points[points.length - 1].valeur);
  return `${points.length} contrôles${seuil}, ${declenchements} déclenchement${declenchements > 1 ? 's' : ''} ; dernière valeur ${derniere}.`;
}

// Phrases envoyées dans la conversation (« Modifier », « Dupliquer » de la fiche) : le mot
// « agent » y figure pour que le serveur propose les outils d'agents (mentionAgents, §3.2) ;
// la modification passe ensuite par la carte « Modification d'agent » (D4)
export const phraseModification = (agent) => `Je veux modifier mon agent « ${agent.nom} » : propose-moi ce que je peux changer.`;
export const phraseDuplication = (agent) => `Crée un nouvel agent sur le modèle de mon agent « ${agent.nom} » ; je te dirai ce qui change.`;

// ── Mode dégradé (cahier, G8) ───────────────────────────────────────

/**
 * Bandeaux du centre des agents : moteur arrêté (GET /statut/agents en 503), Qwen
 * indisponible (tous les modèles sondés de /api/models à disponible: false ; Claude, non
 * sondé, ne compte pas). Moteur inconnu (route absente, réseau) : aucun bandeau.
 * → [{cle, texte}]
 */
export function bandeauxDegrades({ moteur = null, models = [] } = {}) {
  const bandeaux = [];
  if (moteur?.disponible === false) {
    bandeaux.push({ cle: 'moteur', texte: 'Le moteur des agents ne répond pas : les contrôles reprendront à son retour. Vos agents et leurs réglages sont conservés.' });
  }
  const sondes = (Array.isArray(models) ? models : []).filter((m) => typeof m?.disponible === 'boolean');
  if (sondes.length && sondes.every((m) => m.disponible === false)) {
    bandeaux.push({ cle: 'qwen', texte: 'DigiTransLLM est momentanément indisponible : les alertes partent normalement, les rapports d’expertise sont différés.' });
  }
  return bandeaux;
}

// ── Web Push (§6bis) ────────────────────────────────────────────────

/** État affiché sous « Recevoir les notifications sur cet ordinateur ». */
export function etatPushTexte({ prisEnCharge = true, navigateurs = null, refuse = false, ceNavigateur = false } = {}) {
  if (!prisEnCharge) return 'Ce navigateur ne peut pas recevoir les notifications de l’assistant : vos alertes arrivent dans l’application et par e-mail.';
  if (refuse) return 'Notifications refusées sur ce navigateur : autorisez-les dans les réglages du site, puis activez-les de nouveau.';
  const n = Number(navigateurs) || 0;
  if (n > 0) {
    const total = `Actives sur ${n} navigateur${n > 1 ? 's' : ''}`;
    return ceNavigateur ? `${total}, dont celui-ci.` : `${total}.`;
  }
  return 'Pas encore actives : recevez vos alertes même quand l’assistant est fermé.';
}

// ── Bibliothèque des modèles (cahier, « Familles d'agents et bibliothèque ») ──

// Nom technique « domaine-famille » (D12) et nom affiché ; règle ou calendrier par défaut,
// données lues et livrable du cahier, en clair, sans nom d'outil : l'assistant cherche
// lui-même les outils du MCP et fait valider la mission par la carte. Rapports en PDF
// seulement en P1 (décision 8) : le bilan mensuel du cahier, prévu en PowerPoint, est en PDF.
export const MODELES_AGENTS = Object.freeze([
  {
    id: 'tco-veilleur',
    nom: 'Coût total de possession',
    famille: 'veilleur',
    regle: 'Coût total de possession annuel d’un véhicule au-dessus d’un seuil, contrôlé toutes les 6 h',
    donnees: 'Coût total de possession par véhicule et sa décomposition par poste',
    livrable: 'Alerte et rapport d’expertise PDF',
    demande: 'Crée un agent qui surveille toutes les 6 heures le coût total de possession annuel de chaque véhicule et me prévient dès qu’un véhicule dépasse un seuil, avec un rapport d’expertise PDF qui détaille les postes de coût. Propose-moi un seuil d’après les données actuelles.',
  },
  {
    id: 'budget-veilleur',
    nom: 'Suivi du budget',
    famille: 'veilleur',
    regle: 'Dépenses du mois au-delà de 90 %, puis de 100 % du budget',
    donnees: 'Suivi budgétaire du mois en cours',
    livrable: 'Alerte et tableau des postes',
    demande: 'Préviens-moi dès que les dépenses du mois dépassent 90 % du budget, puis 100 %, avec le tableau des postes de dépense.',
  },
  {
    id: 'carburant-veilleur',
    nom: 'Écart de carburant',
    famille: 'veilleur',
    regle: 'Écart entre carburant déclaré et carburant mesuré par le GPS au-delà de 12 % sur 7 jours',
    donnees: 'Pleins déclarés et baisses de carburant mesurées par le GPS',
    livrable: 'Alerte et liste des véhicules suspects',
    demande: 'Surveille chaque jour l’écart entre le carburant déclaré et celui mesuré par le GPS sur les 7 derniers jours, et préviens-moi dès qu’il dépasse 12 % pour un véhicule, avec la liste des véhicules suspects.',
  },
  {
    id: 'conformite-veilleur',
    nom: 'Échéances des documents',
    famille: 'veilleur',
    regle: 'Assurance, visite technique ou carte grise qui expire dans les 30 jours',
    donnees: 'Expirations des assurances et échéances des documents des véhicules',
    livrable: 'Rappel hebdomadaire',
    demande: 'Chaque lundi, envoie-moi le rappel des assurances, des visites techniques et des cartes grises qui expirent dans les 30 prochains jours.',
  },
  {
    id: 'maintenance-veilleur',
    nom: 'Entretiens en retard',
    famille: 'veilleur',
    regle: 'Entretien préventif dépassé, en jours ou en kilomètres',
    donnees: 'Plan d’entretien préventif des véhicules',
    livrable: 'Alerte et planning proposé',
    demande: 'Préviens-moi dès qu’un entretien préventif est dépassé, en jours ou en kilomètres, avec un planning de rattrapage proposé.',
  },
  {
    id: 'securite-sentinelle',
    nom: 'Survitesses',
    famille: 'sentinelle',
    regle: 'Survitesse grave ou répétée d’un conducteur',
    donnees: 'Événements GPS et survitesses des conducteurs',
    livrable: 'Alerte et fiche du conducteur',
    demande: 'Préviens-moi dès qu’un conducteur commet une survitesse grave ou répétée, avec sa fiche conducteur.',
  },
  {
    id: 'zone-sentinelle',
    nom: 'Sorties de zone',
    famille: 'sentinelle',
    regle: 'Entrée ou sortie d’une zone en dehors des heures permises',
    donnees: 'Événements GPS et zones (géo-clôtures)',
    livrable: 'Alerte et carte du trajet',
    demande: 'Alerte-moi dès qu’un véhicule entre dans une zone ou en sort en dehors des heures permises, avec la carte de son trajet.',
  },
  {
    id: 'immobilisation-sentinelle',
    nom: 'Véhicules immobilisés',
    famille: 'sentinelle',
    regle: 'Véhicule hors ligne ou à l’arrêt depuis plus de 48 h',
    donnees: 'Événements GPS et positions des véhicules',
    livrable: 'Alerte et dernière position',
    demande: 'Préviens-moi dès qu’un véhicule est hors ligne ou à l’arrêt depuis plus de 48 heures, avec sa dernière position connue.',
  },
  {
    id: 'bilan-hebdo',
    nom: 'Bilan de la semaine',
    famille: 'rapporteur',
    regle: 'Chaque lundi à 8 h',
    donnees: 'Indicateurs de la semaine écoulée',
    livrable: 'Rapport PDF de 4 pages',
    demande: 'Chaque lundi à 8 h, envoie-moi le bilan de la semaine écoulée de ma flotte dans un rapport PDF de 4 pages.',
  },
  {
    id: 'bilan-mensuel-direction',
    nom: 'Bilan mensuel de direction',
    famille: 'rapporteur',
    regle: 'Le 1er du mois à 7 h',
    donnees: 'Indicateurs du mois et cumul de l’année',
    livrable: 'Rapport PDF d’une dizaine de pages',
    demande: 'Le 1er de chaque mois à 7 h, envoie à la direction le bilan du mois écoulé et le cumul de l’année dans un rapport PDF d’une dizaine de pages.',
  },
  {
    id: 'plan-economies',
    nom: 'Plan d’économies',
    famille: 'analyste',
    regle: 'Une seule fois',
    donnees: '12 mois de coûts, de consommation et de maintenance',
    livrable: 'Rapport d’expertise et plan d’économies chiffré',
    demande: 'Crée un agent analyste qui étudie 12 mois de coûts, de consommation et de maintenance de ma flotte, puis me remet un rapport d’expertise avec un plan d’économies chiffré.',
  },
  {
    id: 'audit-donnees',
    nom: 'Audit des données',
    famille: 'analyste',
    regle: 'Une seule fois',
    donnees: 'Fiabilité des données de la plateforme',
    livrable: 'Rapport des données manquantes et des corrections',
    demande: 'Crée un agent analyste qui contrôle la fiabilité des données de ma flotte et me remet un rapport des données manquantes et des corrections à faire.',
  },
]);

/** Modèles regroupés par famille, dans l'ordre du cahier : [{famille, modeles}]. */
export function modelesParFamille(modeles = MODELES_AGENTS) {
  return Object.keys(FAMILLES)
    .map((famille) => ({ famille, modeles: modeles.filter((m) => m.famille === famille) }))
    .filter((groupe) => groupe.modeles.length);
}
